import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Drawer, Box, Typography, Stack, IconButton, Chip, Paper, Skeleton, Alert,
} from '@mui/material';
import { Close, AccountBalanceWallet, Sync } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';
import SerialNoDataGrid from '../ui/SerialNoDataGrid';
import { getPartnerGodView, type PartnerRow } from '../../services/partnerUserService';
import { formatMoney } from '../../utils/currency';
import { formatRegisteredDateTime } from '../../utils/dateFormat';

interface PartnerWalletTransactionsDrawerProps {
  open: boolean;
  onClose: () => void;
  partner: PartnerRow | null;
}

const FLOW_COLORS: Record<string, 'success' | 'error' | 'default'> = {
  earned: 'success',
  spent: 'error',
};

export default function PartnerWalletTransactionsDrawer({
  open, onClose, partner,
}: PartnerWalletTransactionsDrawerProps) {
  const [page, setPage] = useState(0);
  const pageSize = 25;

  useEffect(() => {
    setPage(0);
  }, [partner?._id]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['partner-wallet-txs', partner?._id, page],
    queryFn: () => getPartnerGodView(partner!._id, { tab: 'wallet', page: page + 1, limit: pageSize }),
    enabled: open && !!partner?._id,
    // Keep the previous page while paging, but never show another partner's rows.
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[1] === partner?._id ? prev : undefined),
  });

  const rows = data?.rows || [];
  const total = data?.total ?? 0;
  const balance = data?.stats?.walletBalance ?? partner?.walletBalance ?? 0;
  const name = `${partner?.firstName || ''} ${partner?.lastName || ''}`.trim() || 'Partner';

  const columns: GridColDef[] = [
    {
      field: 'transactionId',
      headerName: 'Transaction ID',
      width: 130,
      valueGetter: (_v, row) => String(row.transactionId || row._id || '').slice(-8).toUpperCase(),
    },
    {
      field: 'createdAt',
      headerName: 'Date & Time',
      width: 160,
      valueGetter: (_v, row) => formatRegisteredDateTime(row.createdAt as string),
    },
    {
      field: 'flow',
      headerName: 'Type',
      width: 100,
      renderCell: (p) => (
        <Chip
          size="small"
          label={String(p.value || '').replace(/_/g, ' ')}
          color={FLOW_COLORS[String(p.value)] || 'default'}
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 110,
      valueGetter: (_v, row) => {
        const flow = String(row.flow || '');
        const amt = Number(row.amount ?? 0);
        return `${flow === 'spent' ? '−' : '+'}${formatMoney(amt)}`;
      },
    },
    { field: 'description', headerName: 'Description / reason', flex: 1, minWidth: 160 },
    {
      field: 'adjustedByName',
      headerName: 'By',
      width: 130,
      valueGetter: (_v, row) => row.adjustedByName || (row.source === 'system' ? 'System' : '—'),
    },
    {
      field: 'type',
      headerName: 'Category',
      width: 110,
      valueGetter: (_v, row) => String(row.type || '').replace(/_/g, ' '),
    },
    {
      field: 'balanceAfter',
      headerName: 'Balance after',
      width: 120,
      valueGetter: (_v, row) => formatMoney(row.balanceAfter ?? 0),
    },
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 560, md: 720 } } }}
    >
      <Box sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="overline" color="text.secondary">Wallet transactions</Typography>
            <Typography variant="h6" fontWeight={800}>{name}</Typography>
          </Box>
          <Stack direction="row" spacing={0.5}>
            <IconButton size="small" onClick={() => refetch()} title="Refresh">
              <Sync fontSize="small" />
            </IconButton>
            <IconButton onClick={onClose}><Close /></IconButton>
          </Stack>
        </Stack>

        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: 'primary.main', color: 'primary.contrastText', display: 'flex' }}>
                <AccountBalanceWallet fontSize="small" />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Current balance</Typography>
                <Typography variant="h5" fontWeight={800}>{formatMoney(balance)}</Typography>
              </Box>
            </Stack>
            <Box textAlign="right">
              <Typography variant="caption" color="text.secondary" display="block">
                {total} transaction{total === 1 ? '' : 's'}
              </Typography>
              {!isLoading && rows.length > 0 && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Latest: {String(rows[0]?.flow || '') === 'spent' ? '−' : '+'}
                  {formatMoney(Number(rows[0]?.amount ?? 0))}
                </Typography>
              )}
            </Box>
          </Stack>
        </Paper>

        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>Could not load wallet transactions.</Alert>
        )}

        {isLoading ? (
          <Stack spacing={1}>
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} variant="rounded" height={40} />)}
          </Stack>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Transactions ({total})
            </Typography>
            <SerialNoDataGrid
              rows={rows}
              columns={columns}
              getRowId={(r) => String(r._id)}
              paginationModel={{ page, pageSize }}
              pageSizeOptions={[pageSize]}
              rowCount={total}
              paginationMode="server"
              onPaginationModelChange={(m) => setPage(m.page)}
              autoHeight
              disableRowSelectionOnClick
              slots={{
                noRowsOverlay: () => (
                  <Typography color="text.secondary" align="center" sx={{ py: 6 }}>
                    No wallet transactions yet
                  </Typography>
                ),
              }}
            />
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
